import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { translatePaymentType, getPaymentTypeColor } from '../../../utils/paymentTypeTranslator';

dayjs.locale('it');

const getTypeColor = (type) => {
  switch (type) {
    case 'Tesseramento':
      return 'primary';
    case 'nuovo corso':
      return 'success';
    case 'rinnovo corso':
      return 'warning';
    case 'Spesa':
      return 'error';
    default:
      return 'default';
  }
};

const formatMoney = (amount) =>
  `${amount.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`;

const DetailTable = ({ title, rows, emptyLabel }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1 }}>
      {title}
    </Typography>
    {rows.length === 0 ? (
      <Typography variant='body2' color='text.secondary'>
        {emptyLabel}
      </Typography>
    ) : (
      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Descrizione</TableCell>
              <TableCell align='right'>Importo</TableCell>
              <TableCell>Metodo</TableCell>
              <TableCell>Utente</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const user = row.user;
              const fullName = user ? `${user.name || ''} ${user.surname || ''}`.trim() : '';
              return (
                <TableRow key={row.id}>
                  <TableCell>{dayjs(row.createdAt).format('DD/MM/YYYY')}</TableCell>
                  <TableCell>
                    <Chip
                      label={(row.type || '').charAt(0).toUpperCase() + (row.type || '').slice(1)}
                      color={getTypeColor(row.type)}
                      size='small'
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>{row.name || '—'}</Typography>
                    {row.note ? (
                      <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5 }}>
                        {row.note}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align='right'>
                    <Typography
                      variant='body2'
                      fontWeight={500}
                      color={row.amount >= 0 ? 'success.main' : 'error.main'}
                    >
                      {formatMoney(row.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.method ? (
                      <Chip
                        label={translatePaymentType(row.method)}
                        color={getPaymentTypeColor(row.method)}
                        size='small'
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    {fullName ? (
                      <Box>
                        <Typography variant='body2'>{fullName}</Typography>
                        {user?.email ? (
                          <Typography variant='caption' color='text.secondary'>
                            {user.email}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Box>
);

const ModalFinanceMonthDetails = ({ open, onClose, monthLabel, items = [] }) => {
  const { incomes, expenses } = useMemo(() => {
    const inc = [];
    const exp = [];
    items.forEach((row) => {
      if (row.amount >= 0) inc.push(row);
      else exp.push(row);
    });
    const byDate = (a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf();
    inc.sort(byDate);
    exp.sort(byDate);
    return { incomes: inc, expenses: exp };
  }, [items]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>Dettaglio — {monthLabel || ''}</DialogTitle>
      <DialogContent dividers>
        <DetailTable title='Entrate' rows={incomes} emptyLabel='Nessuna entrata in questo mese.' />
        <DetailTable title='Uscite' rows={expenses} emptyLabel='Nessuna uscita in questo mese.' />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant='contained' color='primary'>
          Chiudi
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalFinanceMonthDetails;
